import { useMemo, useRef, useState } from 'react'

import { useErrorToast } from '~/hooks/useErrorToast'
import { now as serverNow } from '~/models/clock'
import type {
  BulkApprovalCase,
  BulkApprovalOutcome,
  BulkFailureMessages,
  BulkItemResult,
} from '~/pages/SpotBidding/models/bulkApproval'
import { summarizeBulkResults } from '~/pages/SpotBidding/models/bulkApproval'
import type { SpotMarketPhase } from '~/pages/SpotBidding/models/marketPhase'
import { calcSpotPhase } from '~/pages/SpotBidding/models/marketPhase'

/** 送る案件 1 件 (楽観ロック基準値は読んだ proto Timestamp のまま渡す)。 */
export type BulkTarget = Pick<BulkApprovalCase, 'id' | 'expectedUpdatedAt'>

/** `confirm` が受ける確定の前提 (`useBulkSelection` の戻り値の一部)。 */
export type BulkConfirmation = {
  selected: readonly BulkApprovalCase[]
  /** 確定できない理由 (undefined なら確定できる)。 */
  confirmBlockedReason: string | undefined
}

type Params<P> = {
  /** 承認待ちの案件 (画面の並び順)。 入口の件数と、 モーダルの案件の元。 */
  pending: readonly P[]
  /**
   * 承認待ちの案件を検証結果付きの案件に写す。 数千グループでも入口の描画が重くならない
   * よう、 モーダルを開いている間だけ呼ぶ。 参照が変わるたびに案件を作り直すので、
   * 呼び出し側は `useCallback` で依存が変わった時だけ変える。
   */
  buildCases: (pending: readonly P[]) => BulkApprovalCase[]
  /** 案件を 1 回の RPC で承認する。 呼び出し側が応答を `BulkItemResult` に揃える。 */
  send: (targets: BulkTarget[]) => Promise<BulkItemResult[]>
  /** 一覧・明細を読み直す (成功・失敗のどちらの後にも 1 回呼ぶ)。 */
  invalidate: () => Promise<unknown>
  /** 承認待ちのうち、 現在ユーザーが承認者に設定されていない案件の数 (入口の補足と、 全件がそうなら入口を出さない判断に使う)。 */
  notApproverCount: number
  /** 入口を無効にする理由 (単件の承認 / 差戻しの `decisionDisabledReason` と同じ policy)。 */
  decisionDisabledReason: string | undefined
  /** 承認できない理由を `phaseAt` の時点で引き直す (単件と同じ policy。 確定時の再判定用)。 */
  decisionBlockedReasonAt: (phaseAt: SpotMarketPhase) => string | undefined
  /** 受渡日 (= 締切の再判定と、 結果表示を受渡日ごとに切り替えるのに使う)。 */
  targetDate: Date
  failureMessages: BulkFailureMessages
  /** RPC error を画面の文にする。 */
  translateError: (err: unknown) => string
  /** モーダルを開く時に呼ぶ (入札制限を取り直す等)。 */
  onOpen?: () => void
  /** 承認できた (`approved`) 案件ごとに呼ぶ。 承認できなかった案件と RPC error では呼ばない。 */
  onDecided?: (requestId: string) => void
}

export type BulkApprovalFlow = {
  /** 承認待ちの案件数 (入口の「N件あります」)。 0 件なら入口を出さない。 */
  pendingCount: number
  /** 承認待ちのうち、 現在ユーザーが承認者に設定されていない案件の数。 */
  notApproverCount: number
  /** 入口を無効にする理由 (undefined なら押せる)。 */
  disabledReason: string | undefined
  isOpen: boolean
  open: () => void
  close: () => void
  /** モーダルに並べる案件 (開いている間のみ。 画面の並び順)。 */
  cases: BulkApprovalCase[]
  /**
   * 選択中の案件を 1 回の RPC で承認する。 選択の状態 (どれを選んだか、 再確認を済ませたか、
   * 0 件でないか) は確認モーダルが `useBulkSelection` で持つ (切替のたびに画面全体を
   * 描き直さないため)。 確定できない理由が残っていれば何もしない (= 画面の無効表示が壊れても
   * 再確認の未完了などを素通ししない)。 ここでは確定時の再判定と送信、 結果の整理を行う。
   */
  confirm: (selection: BulkConfirmation) => Promise<void>
  isRunning: boolean
  /** 直近の実行結果 (表示中の受渡日のもの)。 */
  outcome: BulkApprovalOutcome | undefined
}

/**
 * まとめて承認 (AC-BSP-118) の手順のうち、 開閉・確定時の再判定・1 回の送信・結果の整理を
 * 通常入札・ブロック入札で共有する。 選択と再確認は確認モーダルの `useBulkSelection` が持つ。 違うのは案件の組み立てと RPC だけなので、 呼び出し側
 * (`useSpotBulkApproval` / `useBlockBulkApproval`) がそれを渡す。
 *
 * - 確定時にサーバー由来の現在時刻で締切等を引き直し、 通らなければ RPC を呼ばず閉じる
 * - 送る案件は画面の並び順。 選択は分割せず 1 回の RPC で送る
 * - RPC error の時は承認件数を断定せず、 一覧を読み直して error を出す
 */
export const useBulkApprovalFlow = <P>({
  pending,
  buildCases,
  send,
  invalidate,
  notApproverCount,
  decisionDisabledReason,
  decisionBlockedReasonAt,
  targetDate,
  failureMessages,
  translateError,
  onOpen,
  onDecided,
}: Params<P>): BulkApprovalFlow => {
  const showErrorToast = useErrorToast()

  const [isOpen, setOpen] = useState(false)
  const [isRunning, setRunning] = useState(false)
  const runningRef = useRef(false)
  const [lastOutcome, setLastOutcome] = useState<
    { dateKey: number; outcome: BulkApprovalOutcome } | undefined
  >(undefined)

  const cases = useMemo(
    () => (isOpen ? buildCases(pending) : []),
    [isOpen, buildCases, pending],
  )

  const open = (): void => {
    onOpen?.()
    setOpen(true)
  }
  const close = (): void => {
    setOpen(false)
  }

  const confirm = async (selection: BulkConfirmation): Promise<void> => {
    if (runningRef.current || selection.confirmBlockedReason !== undefined) {
      return
    }
    // 導線の phase は分単位 tick 由来で最大 1 分古い。 確定時はサーバー由来の現在時刻で
    // 単件と同じ policy を引き直し、 通らなければ RPC を呼ばない。
    const blocked = decisionBlockedReasonAt(
      calcSpotPhase(serverNow(), targetDate),
    )
    if (blocked !== undefined) {
      showErrorToast({ title: blocked })
      close()
      return
    }
    const sent = selection.selected
    const targets: BulkTarget[] = sent.map((c) => ({
      id: c.id,
      expectedUpdatedAt: c.expectedUpdatedAt,
    }))
    const dateKey = targetDate.getTime()
    runningRef.current = true
    setRunning(true)
    try {
      let results: BulkItemResult[]
      try {
        results = await send(targets)
      } catch (err) {
        // backend が返した error なら 1 件も承認されていないが、 通信の失敗では結果が
        // 分からない。 どちらも承認件数を断定せず、 読み直した一覧で実状態を見せる。
        void invalidate()
        setLastOutcome({
          dateKey,
          outcome: { kind: 'rpcError', message: translateError(err) },
        })
        close()
        return
      }
      void invalidate()
      for (const r of results) {
        if (r.kind === 'approved') {
          onDecided?.(r.id)
        }
      }
      setLastOutcome({
        dateKey,
        outcome: {
          kind: 'done',
          ...summarizeBulkResults(sent, results, failureMessages),
        },
      })
      close()
    } finally {
      runningRef.current = false
      setRunning(false)
    }
  }

  return {
    pendingCount: pending.length,
    notApproverCount,
    disabledReason: decisionDisabledReason,
    isOpen,
    open,
    close,
    cases,
    confirm,
    isRunning,
    outcome:
      lastOutcome?.dateKey === targetDate.getTime()
        ? lastOutcome.outcome
        : undefined,
  }
}
